import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Globe, Clock, Eye, Activity } from 'lucide-react';

interface UserSession {
  id: string;
  fingerprint: string;
  last_page: string | null;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  pages_visited: number | null;
}

interface UserSessionContextProps {
  fingerprint: string;
}

export const UserSessionContext = ({ fingerprint }: UserSessionContextProps) => {
  const [session, setSession] = useState<UserSession | null>(null);
  const [liveDuration, setLiveDuration] = useState<number>(0);
  const [isLive, setIsLive] = useState(false);

  useEffect(() => {
    const fetchLatestSession = async () => {
      const { data, error } = await supabase
        .from('user_sessions')
        .select('*')
        .eq('fingerprint', fingerprint)
        .order('started_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!error && data) {
        setSession(data);
        
        // Check if session is still active (no ended_at or ended within last 5 mins)
        const isActive = !data.ended_at || 
          (new Date().getTime() - new Date(data.ended_at).getTime()) < 5 * 60 * 1000;
        
        setIsLive(isActive);
        
        if (isActive) {
          // Calculate current duration
          const startTime = new Date(data.started_at).getTime();
          const currentDuration = Math.floor((Date.now() - startTime) / 1000);
          setLiveDuration(currentDuration);
        } else {
          setLiveDuration(data.duration_seconds || 0);
        }
      }
    };

    fetchLatestSession();

    // Set up realtime subscription
    const channel = supabase
      .channel(`session-${fingerprint}`)
      .on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: 'user_sessions',
          filter: `fingerprint=eq.${fingerprint}`
        },
        () => fetchLatestSession()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fingerprint]);

  // Live duration ticker
  useEffect(() => {
    if (!isLive || !session) return;

    const interval = setInterval(() => {
      const startTime = new Date(session.started_at).getTime();
      const currentDuration = Math.floor((Date.now() - startTime) / 1000);
      setLiveDuration(currentDuration);
    }, 1000);

    return () => clearInterval(interval);
  }, [isLive, session]);

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}m ${secs}s`;
    }
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${mins}m`;
  };

  if (!session) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Activity className="w-3 h-3" />
        <span>No session data</span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-3 text-xs">
      {/* Live indicator */}
      {isLive && (
        <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
          </span>
          <span className="font-medium">Live</span>
        </div>
      )}

      {/* Current page */}
      {session.last_page && (
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Globe className="w-3 h-3" />
          <span className="max-w-[150px] truncate" title={session.last_page}>
            {session.last_page === '/' ? 'Home' : session.last_page}
          </span>
        </div>
      )}

      {/* Session duration */}
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <Clock className="w-3 h-3" />
        <span className={isLive ? 'tabular-nums' : ''}>
          {formatDuration(liveDuration)}
        </span>
      </div>

      {/* Pages visited */}
      {session.pages_visited && session.pages_visited > 1 && (
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Eye className="w-3 h-3" />
          <span>{session.pages_visited} pages</span>
        </div>
      )}
    </div>
  );
};
